/**
 * A certificate of completion read back by the office, from Docs.
 *
 * The sibling of `ChangeOrderRecordModal`, for the same two reasons: the
 * customer's `/cert/<token>` link shows them their copy once signed but
 * records nothing for us, and on one still live, opening it as staff records
 * a view no customer made. So this reads the frozen snapshot straight off the
 * row, under the signed-in user's own row level security, and draws it with
 * `CertificateView`, the component the customer signed on (ADR 0032).
 */

import { signedDateWords } from "@roof-hawk/core";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchCertificateRecord } from "../../lib/queries";
import { CertificateView } from "../present/CertificatePage";

export function CertificateRecordModal({
  certificateId,
  onClose,
}: {
  certificateId: string;
  onClose: () => void;
}) {
  const record = useAsync(
    () => fetchCertificateRecord(certificateId),
    [certificateId],
  );
  const document = record.data?.document;
  const signature = record.data?.signature ?? null;
  const signedOn = signedDateWords(signature?.signedDate ?? null);

  return (
    <Modal
      title={t("Certificate of completion")}
      subtitle={
        signature
          ? signedOn
            ? t("Signed by {name} on {date}", {
                name: signature.signerName,
                date: signedOn,
              })
            : t("Signed by {name}", { name: signature.signerName })
          : document
            ? t("Not signed yet")
            : undefined
      }
      onClose={onClose}
      wide
    >
      <ErrorNote error={record.error} />
      {record.loading ? <p className="n">{t("Opening…")}</p> : null}
      {document ? (
        <CertificateView
          document={document}
          signature={signature}
          readOnly
          onSign={() => undefined}
        />
      ) : null}
    </Modal>
  );
}
